import { useState } from 'react';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons/faArrowRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer, Title } from '@mantine/core';
import { FormattedMessage } from 'react-intl';

import { FreightForm } from './components/freight-form/FreightForm';
import { RatesPanel } from './components/rates-panel/RatesPanel';
import { freightBrowserMessages } from './freight-browser-messages';
import styles from './FreightBrowser.module.css';

import type { FreightBrowserProps } from './freight-browser-types';
import type { LtlQuote } from '../../types/ltl';

export const FreightBrowser = ({
  isOpen,
  onClose,
  formState,
  onFormChange,
  shipFromOptions,
  rates,
  isLoadingRates,
  rateError,
  onBrowseRates,
  onSaveQuote,
  onConfigureShipment,
}: FreightBrowserProps) => {
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>(undefined);

  const selectedQuote = rates.find((rate) => rate.offerId === selectedOfferId) ?? null;

  const handleSelectQuote = (quote: LtlQuote) => setSelectedOfferId(quote.offerId);

  return (
    <Drawer
      classNames={{ body: styles.drawerBodySlot, content: styles.drawerContent, header: styles.header }}
      closeOnClickOutside
      data-ignore-root-focus
      onClose={onClose}
      opened={isOpen}
      padding={0}
      position='right'
      size='100%'
      title={
        <Title order={3}>
          <FormattedMessage {...freightBrowserMessages.title} />
        </Title>
      }
    >
      <div className={styles.drawerBody}>
        <div className={styles.formColumn}>
          <FreightForm formState={formState} onFormChange={onFormChange} shipFromOptions={shipFromOptions} />
        </div>
        <div className={styles.ratesColumn}>
          <RatesPanel
            isLoading={isLoadingRates}
            onSelectQuote={handleSelectQuote}
            rateError={rateError}
            rates={rates}
            selectedOfferId={selectedOfferId}
          />
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.footerFormSection}>
          <Button onClick={onBrowseRates}>
            <FormattedMessage {...freightBrowserMessages.browseRates} />
          </Button>
        </div>
        <div className={styles.footerRatesSection}>
          <Button disabled={rates.length === 0} onClick={() => onSaveQuote(selectedQuote)} variant='tertiary'>
            <FormattedMessage {...freightBrowserMessages.saveQuote} />
          </Button>
          <Button
            disabled={!selectedQuote}
            onClick={() => selectedQuote && onConfigureShipment(selectedQuote)}
            rightSection={<FontAwesomeIcon icon={faArrowRight} />}
            variant='outline'
          >
            <FormattedMessage {...freightBrowserMessages.configureShipment} />
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
